import {
  NewsArticleResponse,
  FlatResponse,
  BusinessResponse,
  ParkingResponse,
  DocumentResponse,
  ProjectResponse,
  ProjectFeatureResponse,
  FlatFeatureResponse,
  BusinessFeatureResponse,
  ParkingFeatureResponse,
  BusinessProjectFeatureResponse,
  ParkingProjectFeatureResponse,
  CareersResponse,
  ManagementResponse,
  MortgageResponse,
  PromoBlocksResponse,
  PromoResponse,
  SupportResponse,
  ContactsResponse,
} from './api.types';

const isProduction = () => {
  return process.env.NODE_ENV === 'production';
};

const isHttps = () => {
  return window.location.protocol === 'https:';
};

const getApiUrl = () => {
  const envApiUrl = process.env.REACT_APP_API_URL?.trim();
  if (envApiUrl) {
    return envApiUrl;
  }

  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return 'http://127.0.0.1:8000/api';
  }

  return `${window.location.origin}/api`;
};

const API_CONFIG = {
  BASE_URL: getApiUrl(),
  TIMEOUT: 30000,
  DEFAULT_HEADERS: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  RETRY: {
    ENABLED: true,
    MAX_ATTEMPTS: 3,
    DELAY: 1000,
  }
};

console.log('API Configuration:', {
  BASE_URL: API_CONFIG.BASE_URL,
  NODE_ENV: process.env.NODE_ENV,
  HOSTNAME: window.location.hostname,
  PROTOCOL: window.location.protocol,
  REACT_APP_API_URL: process.env.REACT_APP_API_URL,
  IS_PRODUCTION: process.env.NODE_ENV === 'production',
  CURRENT_URL: window.location.href,
});

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

type OptionData = {
  id: number;
  name: string;
  image: string | null;
};

type SpecialOptionsLookup = Record<string, OptionData>;

const FALLBACK_FILES = {
  news: '/News.json',
  flats: '/Flats.json',
  flatsFeatures: '/FlatsFeatures.json',
  business: '/Business.json',
  businessFeatures: '/BusinessFeatures.json',
  businessProjectFeatures: '/BusinessProjectsFeatures.json',
  parking: '/Parkings.json',
  parkingFeatures: '/ParkingFeatures.json',
  parkingProjectFeatures: '/ParkingProjectsFeatures.json',
  projectsFeatures: '/ProjectsFeatures.json',
  documents: '/Documents.json',
  mortgages: '/Mortgages.json',
  specialOptions: '/SpecialOptions.json',
  promoBlocks: '/PromoBlocks.json',
};

const fallbackCache = new Map<string, Promise<any>>();

const fetchFallbackFile = async (path: string): Promise<any> => {
  if (!fallbackCache.has(path)) {
    const request = fetch(path, {
      headers: {
        'Accept': 'application/json',
      },
    }).then(async response => {
      if (!response.ok) {
        throw new Error(`Fallback file request failed: ${path} (${response.status})`);
      }
      return response.json();
    });
    fallbackCache.set(path, request);
  }
  return fallbackCache.get(path) as Promise<any>;
};

const normalizePublicAssetPath = (value: string | null | undefined): string => {
  if (!value) return '';
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (trimmed.startsWith('http') || trimmed.startsWith('/')) {
    return trimmed;
  }
  return `/${trimmed}`;
};

const normalizeSpecialOptionImage = (value: string | null | undefined): string | null => {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  return trimmed.replace('/images/specialoptions/', '/images/specialOptions/');
};

const isEmptyApiResponse = (value: unknown): boolean => {
  if (value === null || value === undefined) {
    return true;
  }
  if (Array.isArray(value)) {
    return value.length === 0;
  }
  if (typeof value === 'object') {
    return Object.keys(value as Record<string, unknown>).length === 0;
  }
  return false;
};

const parseNumericValue = (value: unknown): number | undefined => {
  if (typeof value === 'number') {
    return isNaN(value) ? undefined : value;
  }
  if (typeof value === 'string') {
    const normalized = value.replace(/\s+/g, '').replace(',', '.');
    if (!normalized) {
      return undefined;
    }
    const parsed = Number(normalized);
    return isNaN(parsed) ? undefined : parsed;
  }
  return undefined;
};

let specialOptionsCache: Promise<SpecialOptionsLookup> | null = null;

const getSpecialOptionsLookup = async (): Promise<SpecialOptionsLookup> => {
  if (!specialOptionsCache) {
    specialOptionsCache = fetchFallbackFile(FALLBACK_FILES.specialOptions)
      .then((raw: any) => {
        const lookup: SpecialOptionsLookup = {};
        if (!Array.isArray(raw)) {
          return lookup;
        }
        raw.forEach((option: any, index: number) => {
          const name = typeof option?.name === 'string' ? option.name : '';
          if (!name || lookup[name]) {
            return;
          }
          lookup[name] = {
            id: typeof option?.id === 'number' ? option.id : index + 1,
            name,
            image: normalizeSpecialOptionImage(
              typeof option?.image === 'string' ? option.image : null
            ),
          };
        });
        return lookup;
      })
      .catch(() => ({}));
  }
  return specialOptionsCache;
};

const normalizeOptionsList = (
  options: unknown,
  lookup?: SpecialOptionsLookup
): OptionData[] => {
  if (!Array.isArray(options)) {
    return [];
  }

  return options
    .map((option: any, index: number) => {
      if (typeof option === 'string') {
        const fromLookup = lookup?.[option];
        return {
          id: fromLookup?.id ?? index + 1,
          name: option,
          image: fromLookup?.image ?? null,
        };
      }

      if (option && typeof option === 'object') {
        const name = typeof option.name === 'string' ? option.name : '';
        const fromLookup = name ? lookup?.[name] : undefined;
        const imageValue =
          typeof option.image === 'string' ? option.image : fromLookup?.image ?? null;

        return {
          id: typeof option.id === 'number' ? option.id : fromLookup?.id ?? index + 1,
          name,
          image: imageValue || null,
        };
      }

      const fallbackName = String(option ?? '');
      const fromLookup = lookup?.[fallbackName];
      return {
        id: fromLookup?.id ?? index + 1,
        name: fallbackName,
        image: fromLookup?.image ?? null,
      };
    })
    .filter(option => option.name);
};

const normalizeNewsList = (raw: any): any[] => {
  if (!Array.isArray(raw)) {
    return [];
  }

  return raw.map((article: any) => {
    const content = Array.isArray(article?.content)
      ? article.content.map((block: any) => ({
          ...block,
          block_type: block?.block_type ?? block?.blockType ?? block?.type,
        }))
      : [];

    return {
      ...article,
      shortDescription: article?.shortDescription ?? article?.short_description ?? '',
      content,
    };
  });
};

const normalizeFlatsList = async (raw: any): Promise<any[]> => {
  const list = Array.isArray(raw?.flats) ? raw.flats : Array.isArray(raw) ? raw : [];
  const specialLookup = await getSpecialOptionsLookup();

  return list.map((flat: any) => {
    const area = parseNumericValue(flat?.area) ?? flat?.area;
    const livingArea = parseNumericValue(flat?.livingArea ?? flat?.living_area) ?? flat?.livingArea ?? flat?.living_area;
    const pricePerM2 = parseNumericValue(flat?.pricePerM2 ?? flat?.price_per_m2) ?? flat?.pricePerM2 ?? flat?.price_per_m2;
    const price = parseNumericValue(flat?.price) ?? flat?.price;
    const floor = parseNumericValue(flat?.floor) ?? flat?.floor;
    const totalFloors = parseNumericValue(flat?.totalFloors ?? flat?.total_floors) ?? flat?.totalFloors ?? flat?.total_floors;
    const entrance = parseNumericValue(flat?.entrance) ?? flat?.entrance;
    const totalEntrances = parseNumericValue(flat?.totalEntrances ?? flat?.total_entrances) ?? flat?.totalEntrances ?? flat?.total_entrances;
    const discountProperty = parseNumericValue(flat?.discountProperty ?? flat?.discount_property) ?? flat?.discountProperty ?? flat?.discount_property;
    const discountFull = parseNumericValue(flat?.discountFull ?? flat?.discount_full) ?? flat?.discountFull ?? flat?.discount_full;

    const windowOptions = normalizeOptionsList(
      flat?.windowOptions ?? flat?.window_options
    );
    const specialOptions = normalizeOptionsList(
      flat?.specialOptions ?? flat?.special_options,
      specialLookup
    );
    const preview3dTour = flat?.preview3dTour ?? flat?.preview3DTour ?? flat?.preview_3d_tour;
    const ceillingHeight = flat?.ceillingHeight ?? flat?.cellingHeight ?? flat?.ceilling_height;

    return {
      ...flat,
      area,
      livingArea,
      pricePerM2,
      price,
      floor,
      totalFloors,
      entrance,
      totalEntrances,
      discountProperty,
      discountFull,
      windowOptions,
      specialOptions,
      preview3dTour,
      ceillingHeight,
      preview_3d_tour: flat?.preview_3d_tour ?? preview3dTour,
    };
  });
};

const normalizeBusinessList = async (raw: any): Promise<any[]> => {
  const list = Array.isArray(raw) ? raw : [];
  const specialLookup = await getSpecialOptionsLookup();

  return list.map((business: any) => {
    const area = parseNumericValue(business?.area) ?? business?.area;
    const pricePerM2 = parseNumericValue(business?.pricePerM2 ?? business?.price_per_m2) ?? business?.pricePerM2 ?? business?.price_per_m2;
    const price = parseNumericValue(business?.price) ?? business?.price;
    const floor = parseNumericValue(business?.floor) ?? business?.floor;
    const totalFloors = parseNumericValue(business?.totalFloors ?? business?.total_floors) ?? business?.totalFloors ?? business?.total_floors;
    const numberPlace = parseNumericValue(business?.numberPlace ?? business?.number_place) ?? business?.numberPlace ?? business?.number_place;
    const totalNumberPlaces = parseNumericValue(business?.totalNumberPlaces ?? business?.total_number_places) ?? business?.totalNumberPlaces ?? business?.total_number_places;
    const cellingHeight = parseNumericValue(business?.cellingHeight ?? business?.celling_height) ?? business?.cellingHeight ?? business?.celling_height;
    const discountProperty = parseNumericValue(business?.discountProperty ?? business?.discount_property) ?? business?.discountProperty ?? business?.discount_property;
    const discountFull = parseNumericValue(business?.discountFull ?? business?.discount_full) ?? business?.discountFull ?? business?.discount_full;

    return {
      ...business,
      area,
      pricePerM2,
      price,
      floor,
      totalFloors,
      numberPlace,
      totalNumberPlaces,
      cellingHeight,
      discountProperty,
      discountFull,
      windowOptions: normalizeOptionsList(
        business?.windowOptions ?? business?.window_options
      ),
      specialOptions: normalizeOptionsList(
        business?.specialOptions ?? business?.special_options,
        specialLookup
      ),
    };
  });
};

const normalizeParkingsList = (raw: any): any[] => {
  const list = Array.isArray(raw) ? raw : [];

  return list.map((parking: any) => {
    const numberPlace = parseNumericValue(parking?.numberPlace ?? parking?.number_place) ?? parking?.numberPlace ?? parking?.number_place;
    const totalFloors = parseNumericValue(parking?.totalFloors ?? parking?.total_floors) ?? parking?.totalFloors ?? parking?.total_floors;
    const parkingType = parking?.parkingType ?? parking?.parking_type;
    const pricePerM2 = parseNumericValue(parking?.pricePerM2 ?? parking?.price_per_m2) ?? parking?.pricePerM2 ?? parking?.price_per_m2;
    const totalNumberPlaces = parseNumericValue(parking?.totalNumberPlaces ?? parking?.total_number_places) ?? parking?.totalNumberPlaces ?? parking?.total_number_places;
    const checkIn = parking?.checkIn ?? parking?.check_in;
    const previewPlan = parking?.previewPlan ?? parking?.preview_plan;
    const discountProperty = parseNumericValue(parking?.discountProperty ?? parking?.discount_property) ?? parking?.discountProperty ?? parking?.discount_property;
    const discountFull = parseNumericValue(parking?.discountFull ?? parking?.discount_full) ?? parking?.discountFull ?? parking?.discount_full;
    const area = parseNumericValue(parking?.area) ?? parking?.area;
    const floor = parseNumericValue(parking?.floor) ?? parking?.floor;
    const price = parseNumericValue(parking?.price) ?? parking?.price;

    return {
      ...parking,
      area,
      floor,
      price,
      numberPlace,
      totalFloors,
      parkingType,
      pricePerM2,
      totalNumberPlaces,
      checkIn,
      previewPlan,
      discountProperty,
      discountFull,
      number_place: numberPlace,
      total_floors: totalFloors,
      parking_type: parkingType,
      price_per_m2: pricePerM2,
      total_number_places: totalNumberPlaces,
      check_in: checkIn,
      preview_plan: previewPlan,
      discount_property: discountProperty,
      discount_full: discountFull,
    };
  });
};

const normalizeProjectFeaturesList = (raw: any): any[] => {
  if (!Array.isArray(raw)) {
    return [];
  }

  return raw.map((project: any, index: number) => {
    const projectId = project?.project?.projectId ?? project?.projectId;
    return {
      ...project,
      project: projectId ? { projectId } : project?.project,
      image: normalizePublicAssetPath(project?.image),
      preview: normalizePublicAssetPath(project?.preview),
      features: Array.isArray(project?.features)
        ? project.features.map((feature: any, featureIndex: number) => ({
            id: typeof feature?.id === 'number' ? feature.id : featureIndex + 1,
            text: feature?.text ?? '',
            image: feature?.image,
          }))
        : [],
      buttons: Array.isArray(project?.buttons)
        ? project.buttons.map((button: any) => ({
            text: button?.text ?? String(button ?? ''),
          }))
        : [],
      id: project?.id ?? index + 1,
    };
  });
};

const normalizeBusinessProjectFeaturesList = (raw: any): any[] => {
  if (!Array.isArray(raw)) {
    return [];
  }

  return raw.map((project: any, index: number) => {
    const projectId = project?.project?.projectId ?? project?.projectId;
    return {
      ...project,
      project: projectId ? { projectId } : project?.project,
      image: normalizePublicAssetPath(project?.image),
      preview: normalizePublicAssetPath(project?.preview),
      features: Array.isArray(project?.features)
        ? project.features.map((feature: any, featureIndex: number) => ({
            id: typeof feature?.id === 'number' ? feature.id : featureIndex + 1,
            text: feature?.text ?? '',
            image: feature?.image,
          }))
        : [],
      id: project?.id ?? index + 1,
    };
  });
};

const normalizeParkingProjectFeaturesList = (raw: any): any[] => {
  if (!Array.isArray(raw)) {
    return [];
  }

  return raw.map((project: any, index: number) => ({
    ...project,
    project: project?.project ?? project?.projectId,
    image: normalizePublicAssetPath(project?.image),
    preview: normalizePublicAssetPath(project?.preview),
    features: Array.isArray(project?.features)
      ? project.features.map((feature: any, featureIndex: number) => ({
          id: typeof feature?.id === 'number' ? feature.id : featureIndex + 1,
          text: feature?.text ?? '',
          image: feature?.image,
        }))
      : [],
    id: project?.id ?? index + 1,
  }));
};

const normalizeDocumentsList = (raw: any): any[] => {
  if (!Array.isArray(raw)) {
    return [];
  }

  return raw.map((doc: any, index: number) => ({
    ...doc,
    file: doc?.file ?? doc?.fileUrl ?? '',
    id: doc?.id ?? index + 1,
  }));
};

type FallbackMatcher = {
  pattern: RegExp;
  file: string;
  normalize: (raw: any) => Promise<any[]> | any[];
  idIndex?: number;
};

const FALLBACK_MATCHERS: FallbackMatcher[] = [
  { pattern: /^\/news\/(\d+)\/?$/, file: FALLBACK_FILES.news, normalize: normalizeNewsList, idIndex: 1 },
  { pattern: /^\/news\/?$/, file: FALLBACK_FILES.news, normalize: normalizeNewsList },
  { pattern: /^\/flats\/(\d+)\/?$/, file: FALLBACK_FILES.flats, normalize: normalizeFlatsList, idIndex: 1 },
  { pattern: /^\/flats\/?$/, file: FALLBACK_FILES.flats, normalize: normalizeFlatsList },
  { pattern: /^\/flats-features\/?$/, file: FALLBACK_FILES.flatsFeatures, normalize: async raw => raw },
  { pattern: /^\/busineses\/(\d+)\/?$/, file: FALLBACK_FILES.business, normalize: normalizeBusinessList, idIndex: 1 },
  { pattern: /^\/busineses\/?$/, file: FALLBACK_FILES.business, normalize: normalizeBusinessList },
  { pattern: /^\/business-features\/?$/, file: FALLBACK_FILES.businessFeatures, normalize: async raw => raw },
  { pattern: /^\/business-project-features\/?$/, file: FALLBACK_FILES.businessProjectFeatures, normalize: normalizeBusinessProjectFeaturesList },
  { pattern: /^\/parkings\/(\d+)\/?$/, file: FALLBACK_FILES.parking, normalize: normalizeParkingsList, idIndex: 1 },
  { pattern: /^\/parkings\/?$/, file: FALLBACK_FILES.parking, normalize: normalizeParkingsList },
  { pattern: /^\/parking-features\/?$/, file: FALLBACK_FILES.parkingFeatures, normalize: async raw => raw },
  { pattern: /^\/parking-project-features\/?$/, file: FALLBACK_FILES.parkingProjectFeatures, normalize: normalizeParkingProjectFeaturesList },
  { pattern: /^\/project-features\/?$/, file: FALLBACK_FILES.projectsFeatures, normalize: normalizeProjectFeaturesList },
  { pattern: /^\/documents\/?$/, file: FALLBACK_FILES.documents, normalize: normalizeDocumentsList },
  { pattern: /^\/mortgages\/?$/, file: FALLBACK_FILES.mortgages, normalize: async raw => (Array.isArray(raw) ? raw : []) },
  { pattern: /^\/promo-blocks\/?$/, file: FALLBACK_FILES.promoBlocks, normalize: async raw => (Array.isArray(raw) ? raw : []) },
];

const getFallbackResponse = async (endpoint: string): Promise<any | undefined> => {
  for (const matcher of FALLBACK_MATCHERS) {
    const match = endpoint.match(matcher.pattern);
    if (!match) {
      continue;
    }

    try {
      const raw = await fetchFallbackFile(matcher.file);
      const normalized = await matcher.normalize(raw);
      const list = Array.isArray(normalized) ? normalized : [];

      if (matcher.idIndex) {
        const idValue = Number(match[matcher.idIndex]);
        const item = list.find(entry => Number(entry?.id) === idValue);
        return item ?? null;
      }

      return list;
    } catch (fallbackError) {
      console.warn('[API] Failed to load fallback data:', fallbackError);
      return undefined;
    }
  }

  return undefined;
};

async function request<T>(
  endpoint: string,
  options?: RequestInit,
  retryCount = 0
): Promise<T> {
  const url = `${API_CONFIG.BASE_URL}${endpoint}`;
  
  if (process.env.NODE_ENV === 'development' || retryCount > 0) {
    console.log(`[API] Making request to: ${url} (attempt ${retryCount + 1})`);
  }
  
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), API_CONFIG.TIMEOUT);
  
  const isLocalhost = url.includes('localhost') || url.includes('127.0.0.1');
  
  const fetchOptions: RequestInit = {
    ...options,
    signal: controller.signal,
    headers: {
      ...API_CONFIG.DEFAULT_HEADERS,
      ...options?.headers,
    },
  };
  
  if (!isLocalhost) {
    fetchOptions.mode = 'cors';
    fetchOptions.credentials = 'omit';
  }
  
  try {
    const response = await fetch(url, fetchOptions);

    clearTimeout(timeoutId);

    if (process.env.NODE_ENV === 'development' || retryCount > 0) {
      console.log(`[API] Response status: ${response.status} for ${endpoint}`);
    }

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}, statusText: ${response.statusText}`);
    }

    const data = await response.json();
    if (process.env.NODE_ENV === 'development' || retryCount > 0) {
      console.log(`[API] Successfully fetched ${endpoint}`, Array.isArray(data) ? `${data.length} items` : 'data');
    }
    if (isEmptyApiResponse(data)) {
      const fallbackData = await getFallbackResponse(endpoint);
      if (fallbackData !== undefined) {
        console.warn(`[API] Using fallback data for ${endpoint} (empty API response)`);
        return fallbackData as T;
      }
    }
    return data as T;
  } catch (error) {
    clearTimeout(timeoutId);
    
    console.error(`[API] Request failed for ${endpoint}:`, {
      error,
      url,
      retryCount,
      userAgent: navigator.userAgent,
      online: navigator.onLine,
    });
    
    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        throw new Error('Request timeout - the server took too long to respond');
      }
      
      if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
        const errorDetails = {
          message: 'Network error occurred',
          possibleCauses: [
            'CORS policy blocking the request',
            'Mixed content (HTTPS site trying to access HTTP API)',
            'Server is not accessible from this domain',
            'Network connectivity issues',
            'Firewall blocking the request'
          ],
          currentUrl: url,
          currentDomain: window.location.origin,
          isHttps: isHttps(),
          userAgent: navigator.userAgent,
          online: navigator.onLine
        };
        
        console.error('[API] Detailed error information:', errorDetails);
        
        if (retryCount === 0 && !url.includes('localhost')) {
          console.log('[API] Attempting fallback to localhost...');
          const fallbackConfig = { ...API_CONFIG };
          fallbackConfig.BASE_URL = 'https://localhost:8000/api';
          
          try {
            const fallbackUrl = `${fallbackConfig.BASE_URL}${endpoint}`;
            console.log(`[API] Trying fallback URL: ${fallbackUrl}`);
            
            const fallbackOptions: RequestInit = {
              ...options,
              headers: {
                ...API_CONFIG.DEFAULT_HEADERS,
                ...options?.headers,
              },
            };
            
            if (!fallbackUrl.includes('localhost') && !fallbackUrl.includes('127.0.0.1')) {
              fallbackOptions.mode = 'cors';
              fallbackOptions.credentials = 'omit';
            }
            
            const fallbackResponse = await fetch(fallbackUrl, fallbackOptions);
            
            if (fallbackResponse.ok) {
              const fallbackData = await fallbackResponse.json();
              console.log(`[API] Fallback successful for ${endpoint}`);
              return fallbackData as T;
            }
          } catch (fallbackError) {
            console.error('[API] Fallback also failed:', fallbackError);
          }
        }
      }
      
      if (API_CONFIG.RETRY.ENABLED && retryCount < API_CONFIG.RETRY.MAX_ATTEMPTS - 1) {
        if (error.message.includes('Failed to fetch') || error.message.includes('NetworkError') || error.message.includes('TypeError')) {
          console.warn(`[API] Retrying request to ${endpoint} (attempt ${retryCount + 2}/${API_CONFIG.RETRY.MAX_ATTEMPTS})`);
          await delay(API_CONFIG.RETRY.DELAY * (retryCount + 1));
          return request<T>(endpoint, options, retryCount + 1);
        }
      }
    }

    const fallbackData = await getFallbackResponse(endpoint);
    if (fallbackData !== undefined) {
      console.warn(`[API] Using fallback data for ${endpoint}`);
      return fallbackData as T;
    }
    
    const enhancedError = new Error(`API request failed for ${endpoint}: ${error instanceof Error ? error.message : 'Unknown error'}`);
    (enhancedError as any).originalError = error;
    (enhancedError as any).url = url;
    (enhancedError as any).retryCount = retryCount;
    
    throw enhancedError;
  }
}

export const api = {
  news: {
    getAll: () => request<NewsArticleResponse[]>('/news/'),
    getById: (id: number) => request<NewsArticleResponse>(`/news/${id}/`),
  },
  
  flats: {
    getAll: () => request<FlatResponse[]>('/flats/'),
    getById: (id: number) => request<FlatResponse>(`/flats/${id}/`),
    getFeatures: () => request<FlatFeatureResponse[]>('/flats-features/'),
  },
  
  business: {
    getAll: () => request<BusinessResponse[]>('/busineses/'),
    getById: (id: number) => request<BusinessResponse>(`/busineses/${id}/`),
    getFeatures: () => request<BusinessFeatureResponse[]>('/business-features/'),
    getProjectFeatures: () => request<BusinessProjectFeatureResponse[]>('/business-project-features/'),
  },
  
  parking: {
    getAll: () => request<ParkingResponse[]>('/parkings/'),
    getById: (id: number) => request<ParkingResponse>(`/parkings/${id}/`),
    getFeatures: () => request<ParkingFeatureResponse[]>('/parking-features/'),
    getProjectFeatures: () => request<ParkingProjectFeatureResponse[]>('/parking-project-features/'),
  },
  
  documents: {
    getAll: () => request<DocumentResponse[]>('/documents/'),
  },
  
  projects: {
    getAll: () => request<ProjectFeatureResponse[]>('/project-features/'),
  },
  
  career: {
    getAll: () => request<CareersResponse[]>('/careers/'),
    getById: (id: number) => request<CareersResponse>(`/careers/${id}/`),
  },
  
  management: {
    getAll: () => request<ManagementResponse[]>('/management/'),
    getById: (id: number) => request<ManagementResponse>(`/management/${id}/`),
  },
  
  mortgage: {
    getAll: () => request<MortgageResponse[]>('/mortgages/'),
  },
  
  promo: {
    getAll: () => request<PromoResponse[]>('/promos/'),
    getById: (id: number) => request<PromoResponse>(`/promos/${id}/`),
  },
  
  promoBlocks: {
    getAll: () => request<PromoBlocksResponse[]>('/promo-blocks/'),
    getById: (id: number) => request<PromoBlocksResponse>(`/promo-blocks/${id}/`),
  },
  
  support: {
    getAll: () => request<SupportResponse[]>('/support/'),
    getById: (id: number) => request<SupportResponse>(`/support/${id}/`),
  },

  contacts: {
    getAll: () => request<ContactsResponse[]>('/contacts/'),
  },
};


export default api;
